import { useMemo, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Refresh, ShieldCheck } from 'iconoir-react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'components/core/theme-provider';
import { Button } from 'components/ui/button';
import { useToast } from 'hooks/use-toast';
import { cn } from 'lib/utils';
import {
  APPROVAL_GRANTS_QUERY_KEY,
  useGetApprovalGrants,
  useRevokeApprovalGrant,
} from 'features/chat/agent-chat-module/hooks/use-gpt-chat';
import type { IApprovalGrant } from 'features/chat/agent-chat-module/services/gpt-chat.service';
import { accountOutlineButtonClassName, accountSectionClassName } from './account-styles';

/*
 * Previous Account Tool permissions UI (Figma 10596:4867 — Built into Klax / Connected apps /
 * New apps with static Always allow / Ask dropdowns) is parked. There is no per-user policy API
 * for that design yet. This tab now uses the standing "Always allow" approval-grants API (same
 * as the Settings dialog Tool permissions empty state / grant list).
 */

interface ToolGroup {
  toolId: string;
  toolName: string;
  grants: IApprovalGrant[];
}

const groupByTool = (grants: IApprovalGrant[]): ToolGroup[] => {
  const groups = new Map<string, ToolGroup>();
  for (const grant of grants) {
    const group = groups.get(grant.toolId);
    if (group) {
      group.grants.push(grant);
    } else {
      groups.set(grant.toolId, {
        toolId: grant.toolId,
        toolName: grant.toolName,
        grants: [grant],
      });
    }
  }
  return [...groups.values()];
};

const formatDate = (value: string) => {
  const ms = Date.parse(value);
  return Number.isNaN(ms) ? value : new Date(ms).toLocaleDateString();
};

/** Standing Always-allow grants from the approval API. */
export function AccountToolPermissionsTab() {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data: grants, isLoading } = useGetApprovalGrants();
  const { mutateAsync: revokeGrant } = useRevokeApprovalGrant();
  const groups = useMemo(() => groupByTool(grants ?? []), [grants]);

  const titleColor = theme === 'dark' ? 'text-true-white' : 'text-grey-950';
  const mutedColor = theme === 'dark' ? 'text-grey-400' : 'text-grey-500';
  const bodyColor = theme === 'dark' ? 'text-true-white' : 'text-grey-950';

  const handleRevoke = async (grant: IApprovalGrant) => {
    const previous = queryClient.getQueryData<IApprovalGrant[]>(APPROVAL_GRANTS_QUERY_KEY);
    queryClient.setQueryData<IApprovalGrant[]>(APPROVAL_GRANTS_QUERY_KEY, (current) =>
      (current ?? []).filter((item) => item.id !== grant.id)
    );
    try {
      await revokeGrant({ grantId: grant.id });
      toast({
        variant: 'success',
        title: t('PERMISSION_REVOKED', { defaultValue: 'Permission revoked' }),
        description: t('PERMISSION_REVOKED_DESCRIPTION', {
          defaultValue: 'This tool will ask for approval again.',
        }),
      });
    } catch {
      queryClient.setQueryData(APPROVAL_GRANTS_QUERY_KEY, previous);
      toast({
        variant: 'destructive',
        title: t('PERMISSION_REVOKE_FAILED', { defaultValue: 'Could not revoke permission' }),
        description: t('PLEASE_TRY_AGAIN', { defaultValue: 'Please try again.' }),
      });
    }
  };

  let body: ReactNode;
  if (isLoading) {
    body = (
      <div className="flex items-center justify-center py-12" data-testid="tool-permissions-loading">
        <Refresh
          width={24}
          height={24}
          strokeWidth={1.5}
          className={cn('animate-spin', mutedColor)}
          aria-hidden
        />
        <span className="sr-only">{t('LOADING', { defaultValue: 'Loading' })}</span>
      </div>
    );
  } else if (groups.length === 0) {
    body = (
      <div
        data-testid="tool-permissions-empty"
        className={cn(
          accountSectionClassName,
          'flex min-h-80 flex-col items-center justify-center gap-2 px-6 py-20 sm:min-h-96 sm:py-24'
        )}
      >
        <ShieldCheck width={20} height={20} strokeWidth={1.5} className={cn('shrink-0', mutedColor)} />
        <p className={cn('max-w-sm text-center text-sm leading-body', mutedColor)}>
          {t('NO_STANDING_PERMISSIONS', {
            defaultValue:
              'No standing permissions. Approvals you grant with Always allow appear here.',
          })}
        </p>
      </div>
    );
  } else {
    body = (
      <div className="flex flex-col gap-4">
        {groups.map((group) => (
          <section key={group.toolId} className={cn(accountSectionClassName, 'overflow-hidden')}>
            <div className="border-b border-grey-100 px-4 py-3 dark:border-border sm:px-6">
              <h3 className={cn('text-sm font-medium leading-body', titleColor)}>
                {group.toolName}
              </h3>
            </div>
            <ul>
              {group.grants.map((grant) => (
                <li
                  key={grant.id}
                  className="flex flex-col gap-3 border-b border-grey-100 px-4 py-3 last:border-b-0 sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:px-6 dark:border-border"
                >
                  <div className="flex min-w-0 flex-col gap-0.5">
                    <span className={cn('truncate text-sm leading-body', bodyColor)}>
                      {grant.actionName === '*'
                        ? t('ALL_ACTIONS', { defaultValue: 'All actions' })
                        : grant.actionName}
                    </span>
                    <span className={cn('text-xs leading-heading', mutedColor)}>
                      {t('GRANTED_ON', { defaultValue: 'Granted on' })} {formatDate(grant.createdDate)}
                    </span>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => handleRevoke(grant)}
                    className={cn(accountOutlineButtonClassName, 'self-start sm:self-auto')}
                  >
                    {t('REVOKE', { defaultValue: 'Revoke' })}
                  </Button>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex min-w-0 flex-col gap-1">
        <h2 className={cn('text-lg font-semibold leading-heading sm:text-xl', titleColor)}>
          {t('TOOL_PERMISSIONS', { defaultValue: 'Tool permissions' })}
        </h2>
        <p className={cn('text-sm leading-body', mutedColor)}>
          {t('TOOL_PERMISSIONS_DESCRIPTION', {
            defaultValue: 'Tools you allowed to run without asking for approval each time.',
          })}
        </p>
      </div>
      {body}
    </div>
  );
}
